import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
// import axios from 'axios';
// import moment from 'moment-timezone';
import apiClient from "../../util/apiClient";
import { updateAccessData } from "../../util/util";

// Async thunk for login
export const login = createAsyncThunk(
  "user/login",
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      // Make API call to login the user
      const response = await apiClient("auth", "POST", { email, password });
      // Return the user data from the API response
      if (!response.data) {
        throw new Error("No data in response");
      }

      // Return the user data from the API response
      return response.data;
    } catch (error: any) {
      // Reject with an error message
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);

// Define user state
interface UserInfo {
  userInfo: any;
  adminUser: boolean;
  companyInfo: any;
  departmentInfo: any;
  access: string[];
  language: string;
  translations: { [key: string]: string };
  masterOptions: any[];
  projects: any[];
  emailCount: number;
}

const initialState: UserInfo = {
  userInfo: {},
  adminUser: false,
  companyInfo: {},
  departmentInfo: {},
  access: [],
  language: "en",
  translations: {},
  masterOptions: [],
  projects: [],
  emailCount: 0,
};

// Timer setup
// let timer: NodeJS.Timeout | null = null;

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    getUser: (state, action: PayloadAction<any>) => {
      const access = action.payload.roleData?.access || [];

      if (action.payload.company && action.payload.userType === "Admin") {
        access.push("adminAccess");
      } else if (action.payload.userType === "Admin") {
        access.push("add-company");
      }

      updateAccessData(access);

      state.userInfo = action.payload;
      state.companyInfo = action.payload.companyData;
      state.departmentInfo = action.payload.departmentData?.id
        ? action.payload.departmentData
        : false;
    },

    setUserData: (state, action: PayloadAction<any>) => {
      Object.assign(state.userInfo, action.payload);
    },
    setProjectsData: (state, action: PayloadAction<any>) => {
      Object.assign(state.projects, action.payload);
    },
    setCompanyData: (state, action: PayloadAction<any>) => {
      Object.assign(state.companyInfo, action.payload);
    },
    updateUserCompanyInfo: (state, action: PayloadAction<any>) => {
  if (state.companyInfo) {
    state.companyInfo = {
      ...state.companyInfo,
      ...action.payload,
    };
  }

  if (state.userInfo) {
    state.userInfo.companyData = {
      ...state.userInfo.companyData,
      ...action.payload,
    };
  }
},

    setDepartmentData: (state, action: PayloadAction<any>) => {
      let dataToAssign = action.payload.departmentData?.id
        ? action.payload.departmentData
        : false;
      Object.assign(state.departmentInfo, dataToAssign);
    },
    setAccessData: (state, action: PayloadAction<any>) => {
      const access = action.payload.roleData?.access || [];
      if (action.payload.company && action.payload.userType === "Admin") {
        access.push("adminAccess");
      } else if (action.payload.userType === "Admin") {
        access.push("add-company");
      }

      state.access = access;
      updateAccessData(access);
      // Optionally save token or user information in local storage
      localStorage.setItem("ACCOUNTING_USER", JSON.stringify(action.payload));
    },

    setTimerCount: (state, action: PayloadAction<any>) => {
      // Timer logic here...
    },

    setUserStateToDefault: (state, action: PayloadAction<any>) => {
      Object.assign(state, {
        userInfo: {},
        adminUser: false,
        companyInfo: {},
        departmentInfo: {},
        access: [],
        language: state.language,
        translations: {},
        masterOptions: [],
        projects: [],
      });
    },

    setTranslation: (
      state,
      action: PayloadAction<{ translations: any; language: string }>
    ) => {
      state.translations = action.payload.translations;
      state.language = action.payload.language;
    },

    setEmailCount: (state, action: PayloadAction<number>) => {
      state.emailCount = action.payload;
      console.log("Email count updated:", state.emailCount);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        // Handle loading state
        state.userInfo = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        // Handle successful login
        state.userInfo = action.payload;
        state.companyInfo = action.payload.companyData;
        state.departmentInfo = action.payload.departmentData?.id
          ? action.payload.departmentData
          : false;

        // Update access data based on user role
        const access = action.payload.roleData?.access || [];
        if (action.payload.company && action.payload.userType === "Admin") {
          access.push("adminAccess");
        } else if (action.payload.userType === "Admin") {
          access.push("add-company");
        }

        state.access = access;
        updateAccessData(access);

        // Optionally save token or user information in local storage
        localStorage.setItem("ACCOUNTING_USER", JSON.stringify(action.payload));
      })
      .addCase(login.rejected, (state, action: PayloadAction<any>) => {
        // Handle login error
        state.userInfo = null;
        console.error("Login failed:", action.payload);
      });
  },
});

export const {
  getUser,
  setUserData,
  setProjectsData,
  setTimerCount,
  setTranslation,
  setCompanyData,
  setDepartmentData,
  setAccessData,
  setUserStateToDefault,
  setEmailCount,
  updateUserCompanyInfo
} = userSlice.actions;

export default userSlice.reducer;
